import React from "react";
import {
  ArrowBack,
  BadgeOutlined,
  CheckCircleOutline,
  Print,
  ShieldOutlined,
  WorkOutline,
} from "@mui/icons-material";
import "./fdcEmploymentAcceptance.css";

const APPLICATION_URL = "/career/field-development-consultant/requirements";

const acknowledgementItems = [
  "I have read the Field Development Consultant Employment, Performance, Commission & Career Progression Terms.",
  "I understand that FDC compensation is performance-linked and that the standard monthly acquisition target is 20 Qualified Clients.",
  "I understand that commission becomes payable only for a Qualified Client who has registered, paid the applicable subscription fee, and been activated.",
  "I understand that the applicable acquisition commission depends on the client's Universe Inventory subscription plan.",
  "I understand the seven-level FDC career progression structure and the six-month performance review framework.",
  "I understand the Performance Suspension and Recovery process that may apply where required performance standards are not met.",
  "I understand the 100-Qualified-Client career protection milestone and the six-month inactivity and retirement framework.",
  "I agree to comply with Universe Inventory Management System policies, lawful instructions, confidentiality requirements, conduct standards, and client-record integrity requirements.",
];

function FormField({ label, name, type = "text", wide = false, placeholder = "" }) {
  return (
    <label className={`fdc-acceptance-field ${wide ? "fdc-acceptance-field--wide" : ""}`}>
      <span>{label}</span>
      <input type={type} name={name} placeholder={placeholder} autoComplete="off" />
    </label>
  );
}

export default function FDCEmploymentAcceptance({
  embedded = false,
  onClose,
}) {
  const printForm = () => {
    if (embedded) {
      document.body.classList.add("fdc-acceptance-modal-printing");
    }

    try {
      window.print();
    } finally {
      if (embedded) {
        document.body.classList.remove("fdc-acceptance-modal-printing");
      }
    }
  };

  return (
    <main className={`fdc-acceptance-page ${embedded ? "fdc-acceptance-page--embedded" : ""}`}>
      <div className="fdc-acceptance-actions no-print">
        {embedded ? (
          <button
            type="button"
            className="fdc-acceptance-actions__back"
            onClick={onClose}
          >
            <ArrowBack aria-hidden="true" />
            Back to Employment Terms
          </button>
        ) : (
          <a className="fdc-acceptance-actions__back" href={APPLICATION_URL}>
            <ArrowBack aria-hidden="true" />
            Back to FDC Application
          </a>
        )}

        <div className="fdc-acceptance-actions__group">
          {embedded && (
            <a
              className="fdc-acceptance-actions__full-page"
              href="/career/field-development-consultant/application/acceptance"
            >
              Open Full Page
            </a>
          )}

          <button
            type="button"
            className="fdc-acceptance-actions__print"
            onClick={printForm}
          >
            <Print aria-hidden="true" />
            Print / Save PDF
          </button>
        </div>
      </div>

      <article className="fdc-acceptance-document">
        <header className="fdc-acceptance-header">
          <div className="fdc-acceptance-header__brand">
            <div className="fdc-acceptance-header__logo" aria-hidden="true">
              <WorkOutline />
            </div>

            <div>
              <strong>Universe Inventory Management System</strong>
              <span>Universe Tech · People Operations</span>
            </div>
          </div>

          <div className="fdc-acceptance-header__status">
            <CheckCircleOutline aria-hidden="true" />
            Employment Document
          </div>
        </header>

        <section className="fdc-acceptance-title-block">
          <div className="fdc-acceptance-title-block__eyebrow">
            <BadgeOutlined aria-hidden="true" />
            FIELD DEVELOPMENT CONSULTANT
          </div>

          <h1>Employee Acceptance Form</h1>
          <p>
            Employment, Performance, Commission &amp; Career Progression Terms
          </p>

          <div className="fdc-acceptance-document-meta">
            <div>
              <span>Position</span>
              <strong>Field Development Consultant (FDC)</strong>
            </div>
            <div>
              <span>Employment Type</span>
              <strong>Employee</strong>
            </div>
            <div>
              <span>Document Version</span>
              <strong>1.0</strong>
            </div>
            <div>
              <span>Application Route</span>
              <strong>/career/field-development-consultant/application</strong>
            </div>
          </div>
        </section>

        <form className="fdc-acceptance-form" onSubmit={(event) => event.preventDefault()}>
          <section className="fdc-acceptance-section">
            <div className="fdc-acceptance-section__heading">
              <span>01</span>
              <div>
                <h2>Employee Information</h2>
                <p>Complete the employee and application identification details.</p>
              </div>
            </div>

            <div className="fdc-acceptance-field-grid">
              <FormField label="Full Legal Name" name="employeeName" wide />
              <FormField label="FDC ID" name="fdcId" />
              <FormField label="FDC Level" name="level" placeholder="e.g. Level 1" />
              <FormField label="State / Branch" name="stateBranch" />
              <FormField label="Application Reference" name="applicationReference" />
              <FormField label="Email Address" name="email" type="email" />
              <FormField label="Phone Number" name="phone" type="tel" />
            </div>
          </section>

          <section className="fdc-acceptance-section">
            <div className="fdc-acceptance-section__heading">
              <span>02</span>
              <div>
                <h2>Terms Acknowledgement</h2>
                <p>
                  Each item should be reviewed before the employee signs this form.
                </p>
              </div>
            </div>

            <div className="fdc-acceptance-checklist">
              {acknowledgementItems.map((item, index) => (
                <label className="fdc-acceptance-check" key={item}>
                  <input type="checkbox" name={`acknowledgement-${index + 1}`} />
                  <span className="fdc-acceptance-check__box" aria-hidden="true" />
                  <span className="fdc-acceptance-check__text">{item}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="fdc-acceptance-section">
            <div className="fdc-acceptance-section__heading">
              <span>03</span>
              <div>
                <h2>Employee Declaration</h2>
                <p>Formal acknowledgement of the employment terms.</p>
              </div>
            </div>

            <div className="fdc-acceptance-declaration">
              <ShieldOutlined aria-hidden="true" />
              <p>
                I, ______________________________________________, confirm that I have read and
                understood the Universe Inventory Management System Field Development Consultant
                Employment, Performance, Commission &amp; Career Progression Terms. I acknowledge
                the performance-based nature of the FDC compensation system and agree to comply
                with the provisions stated in those Terms and applicable company policies.
              </p>
            </div>

            <div className="fdc-acceptance-signature-grid">
              <FormField label="Employee Name" name="signatureName" />
              <FormField label="Date" name="employeeDate" type="date" />

              <label className="fdc-acceptance-signature-line">
                <span>Employee Signature</span>
                <div />
              </label>

              <label className="fdc-acceptance-signature-line">
                <span>Witness / Verification, if applicable</span>
                <div />
              </label>
            </div>
          </section>

          <section className="fdc-acceptance-section fdc-acceptance-section--company">
            <div className="fdc-acceptance-section__heading">
              <span>04</span>
              <div>
                <h2>For Universe Inventory Management System</h2>
                <p>Company authorization and employment-record verification.</p>
              </div>
            </div>

            <div className="fdc-acceptance-field-grid">
              <FormField label="Authorized Officer" name="authorizedOfficer" />
              <FormField label="Position / Title" name="officerPosition" />
              <FormField label="Date" name="companyDate" type="date" />
              <FormField label="Internal Reference" name="internalReference" />
            </div>

            <div className="fdc-acceptance-signature-grid fdc-acceptance-signature-grid--company">
              <label className="fdc-acceptance-signature-line">
                <span>Authorized Signature</span>
                <div />
              </label>

              <label className="fdc-acceptance-signature-line">
                <span>Company Stamp / Seal, if applicable</span>
                <div />
              </label>
            </div>
          </section>
        </form>

        <footer className="fdc-acceptance-footer">
          <p>
            This acceptance form should be retained with the employee's applicable employment
            records together with the corresponding offer letter and Field Development Consultant
            Employment, Performance, Commission &amp; Career Progression Terms.
          </p>
          <span>Universe Inventory Management System · FDC Employee Acceptance · Version 1.0</span>
        </footer>
      </article>

      <div className="fdc-acceptance-mobile-print no-print">
        <button type="button" onClick={printForm}>
          <Print aria-hidden="true" />
          Print / Save PDF
        </button>
      </div>
    </main>
  );
}
